import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Linkedin, Instagram, Twitter, Link2, Copy, Check, Download } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api from "@/lib/api";
import { FadeUp, MaskReveal } from "@/components/Motion";
import { shareLinkedIn, shareInstagram, shareTwitter, copyLink, copyCaptionToClipboard, downloadCoverImage } from "@/lib/shareSocial";
import useSEO, { canonicalFor, breadcrumbLd } from "@/lib/useSEO";

export default function InsightDetail() {
  const { t, i18n } = useTranslation();
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [error, setError] = useState("");
  const [related, setRelated] = useState([]);
  const [copiedCaption, setCopiedCaption] = useState(false);

  useSEO(
    post
      ? {
          title: `${post.title} — Corpora Newsroom`,
          description: post.social_summary || post.excerpt || "",
          canonical: canonicalFor(`/insights/${post.slug}`),
          image: post.cover_image?.startsWith("http") ? post.cover_image : canonicalFor(post.cover_image || ""),
          type: "article",
          jsonLd: [
            {
              "@context": "https://schema.org",
              "@type": "NewsArticle",
              headline: post.title,
              description: post.social_summary || post.excerpt || "",
              image: [post.cover_image?.startsWith("http") ? post.cover_image : canonicalFor(post.cover_image || "")],
              datePublished: post.created_at,
              dateModified: post.updated_at || post.created_at,
              author: { "@type": "Organization", name: post.author || "Corpora Editorial" },
              publisher: {
                "@type": "Organization",
                name: "Corpora Consultants",
                logo: { "@type": "ImageObject", url: canonicalFor("/brand/corpora-og.jpg") },
              },
              mainEntityOfPage: { "@type": "WebPage", "@id": canonicalFor(`/insights/${post.slug}`) },
              keywords: (post.hashtags || []).join(", "),
              articleSection: post.category || "Corporate Law",
              inLanguage: i18n.language || "en",
            },
            breadcrumbLd([
              { name: "Home", url: canonicalFor("/") },
              { name: "Newsroom", url: canonicalFor("/insights") },
              { name: post.title, url: canonicalFor(`/insights/${post.slug}`) },
            ]),
          ],
        }
      : {}
  );

  const url = typeof window !== "undefined" ? window.location.href : "";
  const shareArgs = post
    ? {
        title: post.title,
        summary: post.social_summary || post.excerpt,
        url,
        hashtags: post.hashtags || [],
        coverUrl: post.cover_image,
        slug: post.slug,
      }
    : null;

  useEffect(() => {
    setError("");
    setPost(null);
    api.get(`/insights/${slug}`).then((r) => setPost(r.data)).catch(() => setError(t("insights.notFound")));
    api.get("/insights").then((r) => setRelated(r.data.filter((p) => p.slug !== slug).slice(0, 3))).catch(() => {});
  }, [slug, t, i18n.language]);

  // (Meta tags + title now managed by useSEO hook above.)

  if (error) {
    return (
      <div className="section-pad max-w-[800px] mx-auto px-6 text-center" data-testid="insight-not-found">
        <div className="overline mb-6">404</div>
        <h1 className="display text-5xl text-[var(--bone)]">{error}</h1>
        <Link to="/insights" className="btn-secondary mt-10 inline-flex">
          <ArrowLeft size={14} /> {t("insights.backToAll")}
        </Link>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="section-pad max-w-[800px] mx-auto px-6">
        <div className="font-mono text-sm text-[var(--mute)]">{t("insights.loading")}</div>
      </div>
    );
  }

  return (
    <article data-testid="insight-detail">
      <section className="relative h-[70vh] min-h-[520px] overflow-hidden grain">
        <img src={post.cover_image} alt={post.title} className="absolute inset-0 w-full h-full object-cover" style={{ filter: "brightness(0.45)" }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(10,11,13,0.6) 0%, rgba(10,11,13,0.45) 30%, rgba(10,11,13,0.98) 100%)" }} />
        <div className="relative z-10 h-full max-w-[1100px] mx-auto px-6 md:px-10 flex flex-col justify-end pb-16">
          <div className="overline mb-5">{post.category}</div>
          <h1 className="display text-[var(--bone)]" style={{ fontSize: "clamp(40px, 6.5vw, 92px)" }}>
            <MaskReveal>{post.title}</MaskReveal>
          </h1>
          <div className="mt-8 flex flex-wrap items-center gap-4 font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--mute)]">
            <span>{post.author}</span>
            <span>·</span>
            <span>{post.read_minutes} {t("insights.minRead")}</span>
            <span>·</span>
            <span>
              {new Date(post.created_at).toLocaleDateString(i18n.language || "en", {
                day: "2-digit", month: "long", year: "numeric",
              })}
            </span>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32">
        <div className="max-w-[760px] mx-auto px-6 md:px-10">
          <FadeUp>
            <p className="display text-2xl md:text-3xl italic leading-snug text-[var(--bone)] mb-12 border-l-2 pl-6"
              style={{ borderColor: "var(--pewter)", fontWeight: 300 }}>
              {post.excerpt}
            </p>
          </FadeUp>
          <FadeUp delay={0.1}>
            <div className="article-body">
              {post.content.split("\n").map((line, i) => {
                const trimmed = line.trim();
                if (!trimmed) return null;
                if (trimmed.startsWith("- ")) {
                  return (
                    <p key={i} style={{ marginBottom: "0.4em" }}>
                      <span style={{ color: "var(--pewter)" }}>—</span> {trimmed.slice(2)}
                    </p>
                  );
                }
                return <p key={i}>{trimmed}</p>;
              })}
            </div>
          </FadeUp>

          <FadeUp delay={0.15}>
            <div className="mt-16 pt-10 border-t border-[var(--line)] space-y-8" data-testid="article-share-block">
              {/* Copy-paste-ready social summary */}
              {(post.social_summary || post.excerpt) && (
                <div
                  className="p-6 rounded-md"
                  style={{ background: "rgba(190,170,140,0.04)", border: "1px solid var(--line-2)" }}
                >
                  <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
                    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--mute-2)]">
                      {t("insights.shareReadyLabel")}
                    </span>
                    <button
                      type="button"
                      data-testid="article-copy-caption"
                      onClick={async () => {
                        const ok = await copyCaptionToClipboard(shareArgs);
                        if (ok) {
                          setCopiedCaption(true);
                          toast.success(t("insights.captionCopied"));
                          setTimeout(() => setCopiedCaption(false), 2000);
                        } else {
                          toast.error("Could not copy");
                        }
                      }}
                      className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--pewter)] hover:text-[var(--bone)]"
                    >
                      {copiedCaption ? <Check size={12} /> : <Copy size={12} />}
                      {copiedCaption ? t("insights.copied") : t("insights.copyCaption")}
                    </button>
                  </div>
                  <p className="text-[var(--bone-2)] leading-relaxed text-base mb-4 whitespace-pre-wrap">
                    {post.social_summary || post.excerpt}
                  </p>
                  {Array.isArray(post.hashtags) && post.hashtags.length > 0 && (
                    <div className="flex flex-wrap gap-2" data-testid="article-hashtags">
                      {post.hashtags.map((h) => (
                        <span
                          key={h}
                          className="font-mono text-[10px] uppercase tracking-[0.18em] px-2 py-1"
                          style={{
                            color: "var(--pewter)",
                            border: "1px solid var(--line-2)",
                            borderRadius: 3,
                          }}
                        >
                          #{h}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Share buttons row */}
              <div className="flex flex-wrap items-center gap-3">
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--mute-2)] mr-2">
                  {t("insights.shareLabel")}
                </span>
                <button
                  type="button"
                  data-testid="article-linkedin-share"
                  onClick={() => shareLinkedIn(shareArgs)}
                  className="inline-flex items-center gap-2 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--bone-2)] hover:text-[var(--bone)]"
                  style={{ border: "1px solid var(--line-2)", borderRadius: 4 }}
                >
                  <Linkedin size={14} /> LinkedIn
                </button>
                <button
                  type="button"
                  data-testid="article-instagram-share"
                  onClick={async () => {
                    const copied = await shareInstagram(shareArgs);
                    toast.success(copied ? t("insights.captionCopiedInstagram") : "Opening Instagram…");
                  }}
                  className="inline-flex items-center gap-2 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--bone-2)] hover:text-[var(--bone)]"
                  style={{ border: "1px solid var(--line-2)", borderRadius: 4 }}
                >
                  <Instagram size={14} /> Instagram
                </button>
                <button
                  type="button"
                  data-testid="article-twitter-share"
                  onClick={() => shareTwitter(shareArgs)}
                  className="inline-flex items-center gap-2 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--bone-2)] hover:text-[var(--bone)]"
                  style={{ border: "1px solid var(--line-2)", borderRadius: 4 }}
                >
                  <Twitter size={14} /> X
                </button>
                <button
                  type="button"
                  data-testid="article-copy-link"
                  onClick={async () => {
                    const ok = await copyLink(url);
                    toast.success(ok ? t("insights.linkCopied") : "Could not copy");
                  }}
                  className="inline-flex items-center gap-2 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--bone-2)] hover:text-[var(--bone)]"
                  style={{ border: "1px solid var(--line-2)", borderRadius: 4 }}
                >
                  <Link2 size={14} /> {t("insights.copyLink")}
                </button>
                <button
                  type="button"
                  data-testid="article-download-cover"
                  onClick={async () => {
                    const ok = await downloadCoverImage({
                      coverUrl: post.cover_image,
                      filename: `${post.slug}.jpg`,
                    });
                    toast.success(ok ? t("insights.coverDownloaded") : t("insights.coverOpened"));
                  }}
                  className="inline-flex items-center gap-2 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--bone-2)] hover:text-[var(--bone)]"
                  style={{ border: "1px solid var(--line-2)", borderRadius: 4 }}
                >
                  <Download size={14} /> {t("insights.downloadCover")}
                </button>
                {post.source_link && (
                  <a
                    href={post.source_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--pewter)] hover:text-[var(--bone)] ml-auto"
                    data-testid="article-source-link"
                  >
                    {t("insights.sourceLabel")} ↗
                  </a>
                )}
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      {related.length > 0 && (
        <section className="section-pad border-t border-[var(--line)]">
          <div className="max-w-[1480px] mx-auto px-6 md:px-10">
            <div className="flex items-end justify-between mb-12">
              <FadeUp>
                <div className="overline mb-3">{t("insights.continueReading")}</div>
                <h2 className="display text-4xl md:text-5xl text-[var(--bone)]">
                  {t("insights.relatedTitle")}
                </h2>
              </FadeUp>
              <Link to="/insights" className="btn-ghost">
                {t("insights.allInsights")} <ArrowRight size={14} />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
              {related.map((p) => (
                <Link key={p.id} to={`/insights/${p.slug}`} className="insight-card group block">
                  <div className="overflow-hidden aspect-[4/3] mb-5">
                    <img src={p.cover_image} alt={p.title} className="insight-img w-full h-full object-cover" />
                  </div>
                  <div className="overline mb-2">{p.category}</div>
                  <h3 className="display text-2xl text-[var(--bone)] leading-[1.15] group-hover:text-[var(--pewter)] transition-colors">
                    {p.title}
                  </h3>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </article>
  );
}
